/**
 * Channel list — DMs and groups with online indicators and unread badges.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listStaff, listChannels } from '@/api/team'
import { useTeamStore } from '@/store/team'
import { useAuthStore } from '@/store/auth'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { NewChannelDialog } from './NewChannelDialog'

export function ChannelList() {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const channels = useTeamStore((s) => s.channels)
  const setChannels = useTeamStore((s) => s.setChannels)
  const openChannel = useTeamStore((s) => s.openChannel)
  const onlineUsers = useTeamStore((s) => s.onlineUsers)
  const [showNew, setShowNew] = useState(false)

  // Initial fetch
  useQuery({
    queryKey: ['team-channels'],
    queryFn: async () => {
      const res = await listChannels()
      setChannels(res.channels)
      return res.channels
    },
    staleTime: 30_000,
  })

  const staffQ = useQuery({
    queryKey: ['team-staff'],
    queryFn: async () => {
      const res = await listStaff()
      return res.staff
    },
    staleTime: 60_000,
  })
  const staffMap = new Map((staffQ.data ?? []).map((s) => [s.id, s]))

  const dms = channels.filter((c) => c.kind === 'dm')
  const groups = channels.filter((c) => c.kind === 'group')

  const dmName = (ch: typeof channels[0]) => {
    const otherId = ch.members.find((m) => m !== currentUserId) ?? ch.members[0]
    const staff = staffMap.get(otherId ?? '')
    return staff?.display_name || staff?.email || otherId || 'Unknown'
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-hairline shrink-0">
        <span className="text-sm font-semibold text-ink-900">Messages</span>
        <button
          onClick={() => setShowNew(true)}
          className="w-10 h-10 sm:w-6 sm:h-6 rounded-md flex items-center justify-center text-ink-500 hover:text-ink-900 hover:bg-surface-alt cursor-pointer"
          title="New conversation"
        >
          <Icon name="pen-to-square" variant="solid" className="text-xs" />
        </button>
      </div>

      {/* Online staff */}
      <div className="px-3 py-2 border-b border-hairline/50">
        <div className="text-[9px] uppercase tracking-wider text-ink-400 font-semibold mb-1.5">
          Online — {onlineUsers.length}
        </div>
        <div className="flex flex-wrap gap-1">
          {(staffQ.data ?? [])
            .filter((s) => onlineUsers.includes(s.id) && s.id !== currentUserId)
            .map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-alt text-[10px] text-ink-700"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-success shrink-0" />
                {s.display_name || s.email.split('@')[0]}
              </div>
            ))}
          {onlineUsers.length <= 1 && (
            <span className="text-[10px] text-ink-400 italic">Just you</span>
          )}
        </div>
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {dms.length > 0 && (
          <div className="px-3 pt-2 pb-1">
            <div className="text-[9px] uppercase tracking-wider text-ink-400 font-semibold">
              Direct Messages
            </div>
          </div>
        )}
        {dms.map((ch) => {
          const otherId = ch.members.find((m) => m !== currentUserId)
          const isOnline = otherId ? onlineUsers.includes(otherId) : false
          return (
            <ChannelRow
              key={ch.id}
              label={dmName(ch)}
              unread={ch.unread_count ?? 0}
              online={isOnline}
              onClick={() => openChannel(ch.id)}
            />
          )
        })}

        {groups.length > 0 && (
          <div className="px-3 pt-3 pb-1">
            <div className="text-[9px] uppercase tracking-wider text-ink-400 font-semibold">
              Channels
            </div>
          </div>
        )}
        {groups.map((ch) => (
          <ChannelRow
            key={ch.id}
            label={`# ${ch.name || 'unnamed'}`}
            unread={ch.unread_count ?? 0}
            onClick={() => openChannel(ch.id)}
          />
        ))}

        {channels.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-ink-400">
            No conversations yet.
            <br />
            Start one with the button above.
          </div>
        )}
      </div>

      {showNew && (
        <NewChannelDialog
          staff={staffQ.data ?? []}
          onClose={() => setShowNew(false)}
          onCreated={(ch) => {
            setShowNew(false)
            openChannel(ch.id)
          }}
        />
      )}
    </div>
  )
}

function ChannelRow({
  label,
  unread,
  online,
  onClick,
}: {
  label: string
  unread: number
  online?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-3 sm:py-1.5 hover:bg-surface-alt/50 cursor-pointer text-left"
    >
      {online != null && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full shrink-0',
            online ? 'bg-success' : 'bg-ink-300',
          )}
        />
      )}
      <span className="flex-1 text-xs text-ink-800 truncate">{label}</span>
      {unread > 0 && (
        <span className="shrink-0 min-w-[18px] h-[18px] rounded-full bg-danger text-white text-[10px] font-semibold flex items-center justify-center px-1">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  )
}
